import {Injectable} from '@angular/core';
import {EnvironmentService} from '../environment.service';
import {from, Observable} from 'rxjs';
import {IAddNewSitesAgencyBatch, IAddNewSitesBatch, IDownloadCsvRequestParams} from '../../common/models/interfaces';
import FileSaver from 'file-saver';
import {HttpClient, HttpContext, HttpHeaders} from '@angular/common/http';
import {CsvSiteList} from '../../types/site.type';
import {environment} from '../../../environments/environment';
import {map} from 'rxjs/operators';
import {CUSTOM_SWITCH_TEAM_HEADER_CONTEXT} from 'src/app/common/interceptors/custom-headers.interceptor';
import {ApiClient} from './userway-api.service';
import {
  AddSiteBodyDto,
  AddSiteResponseDto,
  ApiCodeResponse,
  GetMissingImageAltsCsvRequestDto,
  ChangePendingSiteBodyDto,
  GetSitesExistBodyDto,
  ISiteStatus,
  PostSitesBodyParams,
  SiteCreationAccountConfigDto,
  SiteToggleRequestDto,
  UwV2DashboardProductsImageAltsExportCsvCreateParams,
  UwV2SitesSearchListParams,
  V1SitesExistsBatchCreateData,
} from '../../../userway/sdk/UserWayApiClient';
import {AxiosHeaders} from 'axios';
import {AutoUnsubscribe} from '../../decorators/auto-unsubscribe.decorator';
import {CUSTOM_SWITCH_TEAM_HEADER} from '../../common/utils/constants';

@Injectable({
  providedIn: 'root',
})
@AutoUnsubscribe()
export class MySitesApiService {
  private httpSkipHeaders: HttpHeaders = new HttpHeaders({skip: 'true'});

  constructor(
    private env: EnvironmentService,
    private http: HttpClient,
    private apiClient: ApiClient,
  ) {}

  mySites(sitesParams?: UwV2SitesSearchListParams, subAccountUnitId?: string): Observable<any> {
    if (sitesParams?.status === undefined) {
      sitesParams.status = ISiteStatus.ACTIVE;
    }

    const headers = this.createAxiosCustomHeaderContext(subAccountUnitId);

    return from(this.apiClient.api.sites.uwV2SitesSearchList(sitesParams, {headers})).pipe(
      map(({data: {payload}}) => payload),
    );
  }

  mySitesPost(sitesParams?: PostSitesBodyParams, subAccountUnitId?: string): Observable<any> {
    if (sitesParams?.status === undefined) {
      sitesParams.status = ISiteStatus.ACTIVE;
    }

    const headers = this.createAxiosCustomHeaderContext(subAccountUnitId);

    return from(this.apiClient.api.sites.uwV2SitesSearchCreate({}, sitesParams, {headers})).pipe(
      map(({data: {payload}}) => payload),
    );
  }

  getAllSitesStats() {
    const url = `${this.env.apiUrl.apiBase}a11y-data/v0/account/stats`;
    return this.http.get(url);
  }

  removeSite(id: number, subAccountUnitId?: string): Observable<ApiCodeResponse> {
    const params = {
      headers: this.createAxiosCustomHeaderContext(subAccountUnitId),
    };
    const request = this.apiClient.api.sites.sitesRemoveCreate(id.toString(), params);

    return this.apiClient.fetch(request);
  }

  restoreSite(id: number): Observable<ApiCodeResponse> {
    const request = this.apiClient.api.sites.sitesRestoreCreate(id.toString());
    return this.apiClient.fetch(request);
  }

  updatePendingSite(data: ChangePendingSiteBodyDto, action: string): Observable<any> {
    const isActive = action === 'ACTIVE';

    const request = isActive
      ? this.apiClient.api.sites.v1SitesPendingToActiveCreate
      : this.apiClient.api.sites.v1SitesPendingRemoveCreate;

    return this.apiClient.fetch(request(data));
  }

  toggleWidget(state: SiteToggleRequestDto, siteId: number): Observable<ApiCodeResponse> {
    const request = this.apiClient.api.sites.sitesToggleWidgetCreate(siteId.toString(), state);
    return this.apiClient.fetch(request);
  }

  newSite(site: AddSiteBodyDto): Observable<AddSiteResponseDto> {
    const request = this.apiClient.api.sites.v1SitesAddCreate(site);
    return this.apiClient.fetch(request);
  }

  areSitesExist(urls: string[]): Observable<V1SitesExistsBatchCreateData> {
    const data: GetSitesExistBodyDto = {
      urls,
    };

    const request = this.apiClient.api.sites.v1SitesExistsBatchCreate(data);
    return this.apiClient.fetch(request);
  }

  newSitesBatch(site: IAddNewSitesBatch, subAccountUnitId?: string): Observable<any> {
    const url = `${this.env.apiUrl.apiBaseV1}sites/add-batch`;

    return this.http.post(url, site, {
      context: this.createCustomHeaderContext(subAccountUnitId),
    });
  }

  newSitesAgencyBatch(data: IAddNewSitesAgencyBatch, agencySubscriptionId: number): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}uw/v2/exposed/agency/subscriptions/${agencySubscriptionId}/sites/add-batch`;
    return this.http.post(url, data);
  }

  siteCreateConfig(data: SiteCreationAccountConfigDto): Observable<any> {
    const request = this.apiClient.api.accounts.v1AccountSiteCreateConfigCreate({config: data});
    return this.apiClient.fetch(request);
  }

  getPdfDownloadName(siteUrl: string, isFreeReport: boolean) {
    const url = `${environment.apiUserWay}api/a11y-data/v1/site/${encodeURIComponent(siteUrl)}/acc-report-pdf`;
    const params = isFreeReport ? {uwAiWidgetState: 'WIDGET_OFF'} : {};

    return this.http.get<{
      payload: {accReportPdfNames: string[]};
      payloadType: string;
    }>(url, {params});
  }

  downloadPdf(siteUrl: string, fileName: string): void {
    const url = `${environment.apiUserWay}api/a11y-data/v0/site/${encodeURIComponent(
      siteUrl,
    )}/acc-report-pdf/${fileName}`;
    FileSaver.saveAs(url, fileName);
  }

  downloadCsv(formData: any, subAccountUnitId: string): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}uw/v2/sites/import-csv`;
    return this.http.post<CsvSiteList>(url, formData, {
      headers: this.httpSkipHeaders,
      context: this.createCustomHeaderContext(subAccountUnitId),
    });
  }

  downloadAgencyCsv(
    formData: any,
    agencySubscriptionId: any,
    queryParams?: IDownloadCsvRequestParams,
  ): Observable<any> {
    const url = `${this.env.apiUrl.apiBase}uw/v2/exposed/agency/subscriptions/${agencySubscriptionId}/sites/import-csv`;
    return this.http.post<CsvSiteList>(url, formData, {
      params: queryParams as IDownloadCsvRequestParams,
      headers: this.httpSkipHeaders,
    });
  }

  exchangeHostnames(hostnames: string[]): Observable<any> {
    const request = this.apiClient.api.remediationPdf.uwV2SitesExchangeHostnamesCreate({hostnames: hostnames});
    return this.apiClient.fetch(request);
  }

  getListOfSites(email?: string) {
    const url = `${this.env.apiUrl.apiBaseV1}sites/list-of-sites-csv?status=active${email ? '&email=' + encodeURIComponent(email) : ''}`;
    return this.http.get(url);
  }

  exportImgAltsCsv(
    data: GetMissingImageAltsCsvRequestDto,
    query?: UwV2DashboardProductsImageAltsExportCsvCreateParams,
  ) {
    const request = this.apiClient.api.imageAlts.uwV2DashboardProductsImageAltsExportCsvCreate(query, data);
    return this.apiClient.fetch(request);
  }

  private createCustomHeaderContext(subAccountUnitId: string) {
    //Create context with token and value, that we be handled in custom-header.interceptor
    return new HttpContext().set(CUSTOM_SWITCH_TEAM_HEADER_CONTEXT, subAccountUnitId);
  }

  private createAxiosCustomHeaderContext(subAccountUnitId: string) {
    const axiosHeaders = new AxiosHeaders();

    if (subAccountUnitId) {
      axiosHeaders.set(CUSTOM_SWITCH_TEAM_HEADER, subAccountUnitId);
    }

    return axiosHeaders;
  }
}
